(function () {
  if (window.PromoSoundMainCart) return;

  const routesRoot = window.Shopify?.routes?.root || '/';
  const state = {
    busy: false,
    detectedCountryRequested: false,
    euCountries: new Set(),
    euCountryEvidence: new Map(),
    fixedObserver: null,
    chatObserver: null,
    checkoutPlacement: 'order_summary'
  };

  const getRoot = () => document.querySelector('[data-main-cart-section]');

  function getLineItems(lineId) {
    const root = getRoot();
    if (!root) return [];
    return Array.from(root.querySelectorAll('.main-cart__item')).filter((item) => item.dataset.lineId === lineId);
  }

  function announce(message) {
    const status = getRoot()?.querySelector('[data-cart-status]');
    if (!status || !message) return;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  function setBusy(active, trigger) {
    state.busy = active;
    const root = getRoot();
    if (!root) return;

    root.classList.toggle('is-updating', active);
    root.setAttribute('aria-busy', active ? 'true' : 'false');
    root.querySelectorAll('[data-cart-remove], .main-cart__qty-btn').forEach((control) => {
      control.disabled = active;
    });

    if (trigger) {
      trigger.classList.toggle('is-loading', active);
      trigger.closest('.main-cart__item')?.classList.toggle('is-updating', active);
    }
  }

  function updateSharedCart(cartData) {
    document.querySelectorAll('.cart-count').forEach((counter) => {
      counter.textContent = cartData.item_count;
      counter.setAttribute('data-count', cartData.item_count);
    });

    if (window.cart) {
      try {
        window.cart.updateCartCount?.(cartData.item_count);
        window.cart.updateCartSummary?.(cartData);
      } catch (error) {
        console.warn('[Main cart] Mini-cart sync skipped:', error);
      }
    }
  }

  function parseSection(html) {
    if (!html) return null;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return documentFragment.querySelector('[data-main-cart-section]');
  }

  async function fetchSection(sectionId) {
    const response = await fetch(`${routesRoot}cart?section_id=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error(`Section rendering failed with ${response.status}`);
    return response.text();
  }

  function sanitizeNoteValue(value) {
    let v = String(value || '').trim();
    v = v.replace(/^(?:url:\s*)+/i, '');
    if (/^no\s*need$/i.test(v)) v = '';
    return v.trim();
  }

  function restoreNote(noteValue) {
    const note = document.getElementById('CartNote');
    if (note && typeof noteValue === 'string') note.value = sanitizeNoteValue(noteValue);
  }

  function initializeNotePrompt() {
    const note = document.getElementById('CartNote');
    if (!note) return;

    // The note is optional (submit falls back to a default value), so an empty
    // field must not look like a validation error on load.
    note.classList.remove('pulse-outline');
    if (note.dataset.promptInitialized === 'true') return;

    note.dataset.promptInitialized = 'true';
    note.addEventListener('input', () => {
      note.classList.remove('pulse-outline');
    });
  }

  function replaceRenderedRegions(renderedRoot, noteValue) {
    const root = getRoot();
    const currentContent = root?.querySelector('[data-main-cart-content]');
    const nextContent = renderedRoot?.querySelector('[data-main-cart-content]');
    const currentFixed = root?.querySelector('[data-cart-fixed-region]');
    const nextFixed = renderedRoot?.querySelector('[data-cart-fixed-region]');

    if (!root || !currentContent || !nextContent || !currentFixed || !nextFixed) {
      throw new Error('Rendered cart regions are incomplete');
    }

    currentContent.replaceWith(nextContent);
    currentFixed.replaceWith(nextFixed);
    restoreNote(noteValue);
    initializeDynamicUI();
  }

  function applyRenderedSection(sectionId, html) {
    const root = getRoot();
    if (!root || root.dataset.sectionId !== sectionId) return false;

    const renderedRoot = parseSection(html);
    if (!renderedRoot) return false;

    const noteValue = document.getElementById('CartNote')?.value || '';
    replaceRenderedRegions(renderedRoot, noteValue);
    announce(getRoot()?.dataset.updateMessage);
    return true;
  }

  function setChatState(active) {
    const shopifyChat = document.getElementById('shopify-chat');
    if (!shopifyChat) return;
    shopifyChat.style.opacity = active ? '1' : '0';
    shopifyChat.style.pointerEvents = active ? 'auto' : 'none';
  }

  function initializeFixedCheckout() {
    state.fixedObserver?.disconnect();
    state.chatObserver?.disconnect();

    const mainButton = document.getElementById('mainCheckoutBtn');
    const fixedWrapper = document.getElementById('fixedCheckoutWrapper');

    if (!mainButton || !fixedWrapper) {
      setChatState(true);
      return;
    }

    state.fixedObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const fixedIsVisible = !entry.isIntersecting;
        fixedWrapper.style.display = fixedIsVisible ? 'block' : 'none';
        setChatState(!fixedIsVisible);
      });
    });
    state.fixedObserver.observe(mainButton);

    if (!document.getElementById('shopify-chat')) {
      state.chatObserver = new MutationObserver(() => {
        if (!document.getElementById('shopify-chat')) return;
        setChatState(fixedWrapper.style.display !== 'block');
        state.chatObserver?.disconnect();
      });
      state.chatObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  function registerEuCountryEvidence(countryCode, source) {
    const normalizedCountry = (countryCode || '').trim().toUpperCase();
    if (!state.euCountries.has(normalizedCountry)) return;
    state.euCountryEvidence.set(source, { country: normalizedCountry, source });
  }

  function renderEuState() {
    const root = getRoot();
    const immediateStartNotice = root?.querySelector('#immediateStartNotice');
    if (!root || !immediateStartNotice) return;

    const evidence = Array.from(state.euCountryEvidence.values());
    const primaryEvidence = evidence[evidence.length - 1];
    const isEuVisitor = evidence.length > 0;

    root.querySelectorAll('[data-eu-legal-notice]').forEach((notice) => {
      notice.hidden = !isEuVisitor;
    });
    root.querySelectorAll('[data-standard-legal-notice]').forEach((notice) => {
      notice.hidden = isEuVisitor;
    });
    root.querySelectorAll('[data-checkout-label]').forEach((button) => {
      button.textContent = isEuVisitor ? button.dataset.euLabel : button.dataset.standardLabel;
    });
    immediateStartNotice.querySelectorAll('[data-immediate-start-attribute]').forEach((input) => {
      input.disabled = !isEuVisitor;
    });

    const country = root.querySelector('#immediateStartCountry');
    const source = root.querySelector('#immediateStartCountrySource');
    const recordedAt = root.querySelector('#immediateStartRecordedAt');
    if (country) country.value = primaryEvidence?.country || '';
    if (source) source.value = evidence.map((item) => item.source).join('; ');
    if (!isEuVisitor && recordedAt) recordedAt.value = '';
  }

  function initializeEuState() {
    const immediateStartNotice = getRoot()?.querySelector('#immediateStartNotice');
    if (!immediateStartNotice) return;

    state.euCountries = new Set(
      (immediateStartNotice.dataset.euCountries || '')
        .split(',')
        .map((country) => country.trim().toUpperCase())
        .filter(Boolean)
    );

    registerEuCountryEvidence(immediateStartNotice.dataset.activeCountry, 'Shopify active country');
    registerEuCountryEvidence(immediateStartNotice.dataset.customerCountry, 'Customer default address');
    renderEuState();

    if (state.detectedCountryRequested) return;
    state.detectedCountryRequested = true;

    fetch(`${routesRoot}browsing_context_suggestions.json?country[enabled]=true`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Country detection failed with ${response.status}`);
        return response.json();
      })
      .then((data) => {
        registerEuCountryEvidence(data?.detected_values?.country?.handle, 'Shopify browsing context');
        renderEuState();
      })
      .catch(() => {
        // Best-effort detection must never delay cart interaction or checkout.
      });
  }

  function initializeDynamicUI() {
    initializeNotePrompt();
    initializeEuState();
    initializeFixedCheckout();
  }

  function startOptimisticRemoval(lineId) {
    const items = getLineItems(lineId);
    items.forEach((item) => {
      item.style.maxHeight = `${item.offsetHeight}px`;
      item.getBoundingClientRect();
      item.classList.add('is-removing');
      window.requestAnimationFrame(() => {
        item.style.maxHeight = '0px';
      });
    });
    return items;
  }

  function cancelOptimisticRemoval(items) {
    items.forEach((item) => {
      item.classList.remove('is-removing');
      item.style.maxHeight = '';
    });
  }

  async function mutateCart(lineId, quantity, trigger, previousQuantity) {
    if (state.busy) return;

    const root = getRoot();
    const sectionId = root?.dataset.sectionId;
    const noteValue = document.getElementById('CartNote')?.value || '';
    if (!root || !sectionId || !lineId) return;

    const optimisticallyRemovedItems = quantity === 0 ? startOptimisticRemoval(lineId) : [];
    setBusy(true, trigger);

    let mutationApplied = false;

    try {
      const response = await fetch(`${routesRoot}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: lineId,
          quantity,
          sections: [sectionId],
          sections_url: `${routesRoot}cart`
        })
      });

      const cartData = await response.json();
      if (!response.ok) throw new Error(cartData.description || cartData.message || `Cart update failed with ${response.status}`);
      mutationApplied = true;

      let sectionHtml = cartData.sections?.[sectionId] || null;
      if (!sectionHtml) sectionHtml = await fetchSection(sectionId);

      const renderedRoot = parseSection(sectionHtml);
      if (!renderedRoot) throw new Error('Cart section could not be parsed');

      replaceRenderedRegions(renderedRoot, noteValue);
      updateSharedCart(cartData);
      announce(getRoot()?.dataset.updateMessage);

      // Shopify інколи рендерить bundled-секцію ДО перерахунку автознижок:
      // рядок уже видалений, а Discount/Total у HTML старі. cartData.total_price
      // авторитетний — при розходженні перерендерюємо секцію свіжим запитом.
      const renderedTotal = Number(getRoot()?.querySelector('[data-main-cart-content]')?.dataset.cartTotal);
      if (Number.isFinite(renderedTotal) && Number.isFinite(cartData.total_price)
          && renderedTotal !== cartData.total_price) {
        setTimeout(async () => {
          try {
            const freshHtml = await fetchSection(sectionId);
            applyRenderedSection(sectionId, freshHtml);
          } catch (e) { /* не критично — наступна мутація вирівняє */ }
        }, 700);
      }

      const nextControl = quantity === 0
        ? getRoot()?.querySelector('[data-cart-remove]')
        : getLineItems(lineId)[0]?.querySelector(`[data-action="${trigger?.dataset.action || 'increase'}"]`);
      nextControl?.focus({ preventScroll: true });
    } catch (error) {
      console.error('[Main cart] Update failed:', error);
      announce(root.dataset.errorMessage);

      if (mutationApplied) {
        sessionStorage.setItem('promosound-cart-note', noteValue);
        window.location.assign(`${routesRoot}cart`);
        return;
      }

      if (Number.isInteger(previousQuantity)) {
        getLineItems(lineId).forEach((lineItem) => {
          lineItem.querySelectorAll('[data-qty]').forEach((display) => {
            display.textContent = previousQuantity;
          });
        });
      }
      cancelOptimisticRemoval(optimisticallyRemovedItems);
    } finally {
      setBusy(false, trigger);
    }
  }

  document.addEventListener('promosound:cart-section-rendered', async (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    try {
      const html = event.detail?.html || await fetchSection(sectionId);
      applyRenderedSection(sectionId, html);
    } catch (error) {
      console.warn('[Main cart] External section sync skipped:', error);
    }
  });

  document.addEventListener('click', (event) => {
    const fixedButton = event.target.closest('#fixedCheckoutBtn');
    if (fixedButton) {
      const cartForm = document.getElementById('mainCartForm');
      const mainButton = document.getElementById('mainCheckoutBtn');
      state.checkoutPlacement = 'mobile_fixed';
      if (cartForm && mainButton) cartForm.requestSubmit(mainButton);
      return;
    }

    if (event.target.closest('#mainCheckoutBtn')) {
      state.checkoutPlacement = 'order_summary';
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    const quantityButton = event.target.closest('.main-cart__qty-btn');
    const trigger = removeButton || quantityButton;
    if (!trigger || !getRoot()?.contains(trigger)) return;

    const item = trigger.closest('.main-cart__item');
    const lineId = item?.dataset.lineId;
    if (!lineId) return;

    if (removeButton) {
      mutateCart(lineId, 0, removeButton);
      return;
    }

    const currentQuantity = Number.parseInt(item.querySelector('[data-qty]')?.textContent || '0', 10);
    const nextQuantity = quantityButton.dataset.action === 'increase' ? currentQuantity + 1 : currentQuantity - 1;
    if (Math.sign(nextQuantity) === -1) return;

    getLineItems(lineId).forEach((lineItem) => {
      lineItem.querySelectorAll('[data-qty]').forEach((display) => {
        display.textContent = nextQuantity;
      });
    });
    mutateCart(lineId, nextQuantity, quantityButton, currentQuantity);
  });

  document.addEventListener('submit', (event) => {
    if (event.target.id !== 'mainCartForm') return;

    const root = getRoot();
    const recordedAt = root?.querySelector('#immediateStartRecordedAt');
    const noteField = root?.querySelector('#CartNote');

    if (recordedAt && !recordedAt.disabled) recordedAt.value = new Date().toISOString();
    if (noteField) {
      const note = sanitizeNoteValue(noteField.value);
      // Пусте поле не блокує чекаут — лише коротка пульсація-нагадування.
      if (!note) noteField.classList.add('pulse-outline');
      const hiddenNote = root?.querySelector('#CartNoteHidden');
      if (hiddenNote) hiddenNote.value = note ? `url:${note}` : 'url:No need';
      // видиме поле не мутуємо — службовий формат живе тільки в hidden note
    }

    const cartItemCount = Number(document.querySelector('.cart-count')?.textContent || 0);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cart_checkout_cta_click',
      placement: state.checkoutPlacement,
      cart_item_count: cartItemCount
    });
    if (typeof window.psGtag === 'function') {
      window.psGtag('event', 'cart_checkout_cta_click', {
        placement: state.checkoutPlacement,
        cart_item_count: cartItemCount
      });
    }
    if (typeof window.clarity === 'function') {
      window.clarity('event', `cart_checkout_${state.checkoutPlacement}`);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    const initialNote = document.getElementById('CartNote');
    if (initialNote && initialNote.value) initialNote.value = sanitizeNoteValue(initialNote.value);
    const storedNote = sessionStorage.getItem('promosound-cart-note');
    if (storedNote !== null) {
      restoreNote(storedNote);
      sessionStorage.removeItem('promosound-cart-note');
    }
    initializeDynamicUI();
  });

  window.PromoSoundMainCart = { initialize: initializeDynamicUI };
})();
